import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { AvatarWithStatus } from '@/components/ui/avatar-with-status';
import { LazyImage } from '@/components/ui/lazy-image';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useProjects } from '@/contexts/ProjectsContext';
import { Mail, CalendarPlus, ListTodo, StickyNote, X, Paperclip, RotateCcw, FileText, Image } from 'lucide-react';
import { useCreateNoteMutation } from '@/hooks/useNotesQuery';
import { cn } from '@/lib/utils';

// --- Custom Modal Components ---
const Modal = ({ isOpen, onClose, children }: { isOpen: boolean; onClose: () => void; children: ReactNode }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-transparent" onClick={onClose} />
      <div className="relative bg-background rounded-lg shadow-lg max-w-lg w-full mx-4 max-h-[90dvh] overflow-y-auto">
        {children}
      </div>
    </div>
  );
};

const ModalHeader = ({ children, onClose }: { children: ReactNode; onClose: () => void }) => (
  <div className="flex items-center justify-between p-6 border-b">
    <div className="flex items-center space-x-2">
      {children}
    </div>
    <button
      onClick={onClose}
      className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
    >
      <X className="h-4 w-4" />
      <span className="sr-only">Close</span>
    </button>
  </div>
);

const ModalTitle = ({ children }: { children: ReactNode }) => (
  <h2 className="text-lg font-semibold leading-none tracking-tight">{children}</h2>
);

const ModalDescription = ({ children }: { children: ReactNode }) => (
  <p className="text-sm text-muted-foreground">{children}</p>
);

const ModalContent = ({ children }: { children: ReactNode }) => (
  <div className="p-6">{children}</div>
);

const ModalFooter = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 p-6 border-t">
    {children}
  </div>
);

// --- Types ---
export type GlobalDialogAction =
  | { type: 'create-event' }
  | { type: 'new-task'; data?: { projectId?: string; boardId?: string; title?: string; clarifyingInfo?: string; sourceUrl?: string; sourceTitle?: string; sourceText?: string; sourceType?: string } }
  | { type: 'add-note'; data?: { title?: string; content?: string; sourceUrl?: string; sourceTitle?: string; sourceText?: string; sourceType?: string } }
  | { type: 'custom'; key: string; data?: any };

interface GlobalDialogContextValue {
  openDialog: (action: GlobalDialogAction) => void;
  closeDialog: () => void;
}

const GlobalDialogContext = createContext<GlobalDialogContextValue | undefined>(undefined);

export const useGlobalDialog = () => {
  const ctx = useContext(GlobalDialogContext);
  if (!ctx) throw new Error('useGlobalDialog must be used within GlobalDialogProvider');
  return ctx;
};

// --- Main Provider ---
// --- Dialog Content Renderers ---
// 1. Create Event
const CreateEventDialog = ({ onClose }: { onClose: () => void }) => {
  // ESC key handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createEventMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('calendar_events')
        .insert({
          title,
          description,
          start_date: startDate,
          end_date: endDate,
          user_id: user.id,
        });
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      toast({ title: 'Event created!' });
      queryClient.invalidateQueries({ queryKey: ['calendarEvents'] });
      onClose();
    },
    onError: () => {
      toast({ title: 'Failed to create event', variant: 'destructive' });
    },
  });
  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        createEventMutation.mutate();
      }}
    >
      <ModalHeader onClose={onClose}>
        <ModalTitle>
          <CalendarPlus className="inline-block mr-2" /> Create Event
        </ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-3">
          <Input placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} />
          <Textarea placeholder="Description" value={description} onChange={e => setDescription(e.target.value)} />
          <div className="flex gap-2">
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
        </div>
      </ModalContent>
      <ModalFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={createEventMutation.isPending}>
          {createEventMutation.isPending ? 'Creating...' : 'Create Event'}
        </Button>
      </ModalFooter>
    </form>
  );
};

// 3. New Task
const NewTaskDialog = ({ onClose, initialData }: { onClose: () => void; initialData?: { projectId?: string; boardId?: string; title?: string; clarifyingInfo?: string; sourceUrl?: string; sourceTitle?: string; sourceText?: string; sourceType?: string } }) => {
  // ESC key handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const { user } = useAuth();
  const { activeProjects } = useProjects();
  const [selectedProject, setSelectedProject] = useState<string>(initialData?.projectId || '');
  const [selectedBoard, setSelectedBoard] = useState<string>(initialData?.boardId || '');
  const [title, setTitle] = useState(initialData?.title || '');
  const [clarifyingInfo, setClarifyingInfo] = useState(initialData?.clarifyingInfo || initialData?.sourceText || '');
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<{
    url: string;
    type: string;
    id: string;
    name: string;
  } | null>(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch boards for selected project
  const { data: boards = [] } = useQuery({
    queryKey: ['kanbanBoards', selectedProject],
    queryFn: async () => {
      if (!selectedProject) return [];

      // Fetch existing boards
      const { data: existingBoards, error: fetchError } = await supabase
        .from('kanban_boards')
        .select('*')
        .eq('project_id', selectedProject)
        .order('order', { ascending: true });

      if (fetchError) throw fetchError;

      if (!existingBoards || existingBoards.length === 0) {
        // Create default boards if none exist
        const defaultBoards = [
          {
            project_id: selectedProject,
            title: "To Do",
            color: "#e2e8f0",
            order: 0,
            category: "default"
          },
          {
            project_id: selectedProject,
            title: "In Progress",
            color: "#93c5fd",
            order: 1,
            category: "default"
          },
          {
            project_id: selectedProject,
            title: "Completed",
            color: "#86efac",
            order: 2,
            category: "default"
          }
        ];

        const { data: insertedBoards, error: insertError } = await supabase
          .from('kanban_boards')
          .insert(defaultBoards)
          .select();

        if (insertError) throw insertError;
        return insertedBoards || [];
      } else {
        return existingBoards;
      }
    },
    enabled: !!selectedProject,
  });

  // File upload function (same as KanbanBoard)
  const uploadFile = async (file: File) => {
    if (!selectedProject || !user) return;

    setIsUploadingFile(true);

    try {
      // Auto-detect file type and convert accordingly
      const newNodeType = file.type.startsWith('image/') ? 'image' : 'file';

      // Create a safe filename
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 9);
      const fileName = file.name;
      const sanitizedName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${selectedProject}/flowmap/${timestamp}-${randomString}-${sanitizedName}`;

      // Upload new file to Supabase storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('project_files')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('project_files')
        .getPublicUrl(filePath);

      // Create entry in uploaded_files table
      const { data: fileEntry, error: dbError } = await supabase
        .from('uploaded_files')
        .insert({
          user_id: user.id,
          project_id: selectedProject,
          filename: fileName,
          file_path: filePath,
          file_type: file.type,
          storage_path: filePath,
          content_type: file.type,
          file_size: file.size,
          external_url: publicUrl
        })
        .select()
        .single();

      if (dbError) throw dbError;

      setUploadedFile({
        url: publicUrl,
        type: file.type,
        id: fileEntry.id,
        name: fileName
      });

    } catch (error: any) {
      console.error('Error uploading file:', error);
      toast({
        title: "File Upload Failed",
        description: error.message || "An error occurred while uploading the file. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingFile(false);
    }
  };

  // Create task mutation (same logic as KanbanBoard)
  const createTaskMutation = useMutation({
    mutationFn: async () => {
      if (!selectedProject || !selectedBoard) {
        throw new Error("Please select a project and board");
      }

      const newId = `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Get current flow map
      const { data: mapData, error: fetchError } = await supabase
        .from('project_maps')
        .select('flow_map')
        .eq('project_id', selectedProject)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;

      const flowMap = mapData?.flow_map as any || { nodes: [], edges: [], mapNodes: [] };

      // Calculate the next order number for this board
      const tasksInBoard = flowMap.mapNodes?.filter((node: any) => node.boardId === selectedBoard) || [];
      const maxOrder = tasksInBoard.length > 0
        ? Math.max(...tasksInBoard.map((node: any) => node.order ?? 0))
        : -1;
      const nextOrder = maxOrder + 1;

      const newMapNode = {
        id: newId,
        text: title,
        children: [],
        isCompleted: false,
        clarifyingInfo,
        position: { x: 100, y: 100 },
        nodeType: uploadedFile ? (uploadedFile.type.startsWith('image/') ? 'image' : 'file') : 'text',
        boardId: selectedBoard,
        order: nextOrder,
        fileUrl: uploadedFile?.url,
        fileType: uploadedFile?.type,
        fileId: uploadedFile?.id,
        fileName: uploadedFile?.name
      };

      flowMap.mapNodes = [...(flowMap.mapNodes || []), newMapNode];

      if (flowMap.nodes) {
        flowMap.nodes.push({
          id: newId,
          type: newMapNode.nodeType === 'file' || newMapNode.nodeType === 'image' ? 'file' : 'custom',
          position: { x: 100, y: 100 },
          data: newMapNode
        });
      }

      // Save updated flow map (create if doesn't exist)
      if (mapData) {
        const { error: updateError } = await supabase
          .from('project_maps')
          .update({ flow_map: flowMap as any })
          .eq('project_id', selectedProject);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('project_maps')
          .insert({
            project_id: selectedProject,
            flow_map: flowMap as any
          });

        if (insertError) throw insertError;
      }

      return newId;
    },
    onSuccess: () => {
      toast({ title: 'Task created successfully!' });
      queryClient.invalidateQueries({ queryKey: ['kanbanTasks', selectedProject] });
      queryClient.invalidateQueries({ queryKey: ['projectMap', selectedProject] });
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to create task',
        description: error.message,
        variant: 'destructive'
      });
    },
  });

  const availableProjects = activeProjects;

  useEffect(() => {
    if (!selectedProject && initialData?.projectId) {
      setSelectedProject(initialData.projectId);
    }
  }, [initialData?.projectId, selectedProject]);

  useEffect(() => {
    if (!selectedBoard && initialData?.boardId && boards.some((board) => board.id === initialData.boardId)) {
      setSelectedBoard(initialData.boardId);
    }
  }, [boards, initialData?.boardId, selectedBoard]);

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        if (!selectedProject || !selectedBoard || !title.trim()) {
          toast({
            title: 'Missing Information',
            description: 'Please select a project, board, and enter a task title.',
            variant: 'destructive'
          });
          return;
        }
        createTaskMutation.mutate();
      }}
    >
      <ModalHeader onClose={onClose}>
        <ModalTitle>
          <ListTodo className="inline-block mr-2" /> New Task
        </ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-4">
          {/* Project Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Project</label>
            <Select value={selectedProject} onValueChange={(value) => {
              setSelectedProject(value);
              setSelectedBoard(''); // Reset board selection when project changes
            }}>
              <SelectTrigger>
                <SelectValue placeholder="Select a project" />
              </SelectTrigger>
              <SelectContent>
                {availableProjects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Board Selection */}
          {selectedProject && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Board</label>
              <Select value={selectedBoard} onValueChange={setSelectedBoard}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a board" />
                </SelectTrigger>
                <SelectContent>
                  {boards.map((board) => (
                    <SelectItem key={board.id} value={board.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: board.color }}
                        />
                        {board.title}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Task Title */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Task Title</label>
            <Input
              placeholder="Enter task title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
            />
          </div>

          {/* Clarifying Info */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Description (Optional)</label>
            <Textarea
              placeholder="Add any clarifying information..."
              value={clarifyingInfo}
              onChange={e => setClarifyingInfo(e.target.value)}
              rows={3}
            />
          </div>

          {(initialData?.sourceUrl || initialData?.sourceTitle || initialData?.sourceType) && (
            <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground space-y-1">
              <div className="font-medium text-foreground">Source</div>
              {initialData?.sourceTitle && <div className="truncate">Title: {initialData.sourceTitle}</div>}
              {initialData?.sourceUrl && <div className="truncate">URL: {initialData.sourceUrl}</div>}
              {initialData?.sourceType && <div>Type: {initialData.sourceType}</div>}
            </div>
          )}

          {/* File Attachment */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Attachment (Optional)</label>
            {!uploadedFile ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = '*/*';
                  input.onchange = async (e) => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    if (file) {
                      await uploadFile(file);
                    }
                  };
                  input.click();
                }}
                disabled={!selectedProject || isUploadingFile}
                className="w-full"
              >
                {isUploadingFile ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600 mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Paperclip className="h-4 w-4 mr-2" />
                    Add Attachment
                  </>
                )}
              </Button>
            ) : (
              <div className="space-y-2">
                {/* File Preview */}
                {uploadedFile.type.startsWith('image/') && uploadedFile.url && (
                  <div className="relative rounded-md overflow-hidden bg-muted/20 border">
                    <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                      <LazyImage
                        src={uploadedFile.url}
                        alt={uploadedFile.name}
                        className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                        rootMargin="50px"
                        draggable="false"
                      />
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2 p-2 bg-muted rounded-md">
                  {uploadedFile.type.startsWith('image/') ? (
                    <Image className="h-4 w-4 text-blue-500" />
                  ) : (
                    <FileText className="h-4 w-4 text-purple-500" />
                  )}
                  <span className="text-sm">{uploadedFile.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setUploadedFile(null)}
                    className="h-6 w-6 p-0 ml-auto"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </ModalContent>
      <ModalFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button
          type="submit"
          disabled={createTaskMutation.isPending || !selectedProject || !selectedBoard || !title.trim()}
        >
          {createTaskMutation.isPending ? 'Creating...' : 'Create Task'}
        </Button>
      </ModalFooter>
    </form>
  );
};

// 4. Add Note
const AddNoteDialog = ({ onClose, initialData }: { onClose: () => void; initialData?: { title?: string; content?: string; sourceUrl?: string; sourceTitle?: string; sourceText?: string; sourceType?: string } }) => {
  // ESC key handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const { user } = useAuth();
  const [title, setTitle] = useState(initialData?.title || '');
  const [content, setContent] = useState(initialData?.content || initialData?.sourceText || '');
  const { toast } = useToast();
  const createNoteMutation = useCreateNoteMutation();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || !title.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a title for your note.',
        variant: 'destructive'
      });
      return;
    }

    createNoteMutation.mutate({
      user_id: user.id,
      title: title.trim(),
      content: content.trim() ? {
        html: `<p>${content.trim().replace(/\n/g, '</p><p>')}</p>`,
        version: '1.0',
        type: 'rich-text',
        lastModified: new Date().toISOString()
      } : undefined,
      tags: []
    });
  };

  useEffect(() => {
    if (createNoteMutation.isSuccess) {
      toast({ title: 'Note created successfully!' });
      onClose();
    }
  }, [createNoteMutation.isSuccess, onClose, toast]);

  useEffect(() => {
    if (createNoteMutation.isError) {
      toast({
        title: 'Failed to create note',
        description: 'Please try again.',
        variant: 'destructive'
      });
    }
  }, [createNoteMutation.isError, toast]);

  return (
    <form onSubmit={handleSubmit}>
      <ModalHeader onClose={onClose}>
        <ModalTitle>
          <StickyNote className="inline-block mr-2" /> Add Note
        </ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Title</label>
            <Input
              placeholder="Note title..."
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Content (Optional)</label>
            <Textarea
              placeholder="Your note content..."
              value={content}
              onChange={e => setContent(e.target.value)}
              rows={6}
            />
          </div>

          {(initialData?.sourceUrl || initialData?.sourceTitle || initialData?.sourceType) && (
            <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground space-y-1">
              <div className="font-medium text-foreground">Source</div>
              {initialData?.sourceTitle && <div className="truncate">Title: {initialData.sourceTitle}</div>}
              {initialData?.sourceUrl && <div className="truncate">URL: {initialData.sourceUrl}</div>}
              {initialData?.sourceType && <div>Type: {initialData.sourceType}</div>}
            </div>
          )}
        </div>
      </ModalContent>
      <ModalFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={createNoteMutation.isPending || !title.trim()}>
          {createNoteMutation.isPending ? 'Creating...' : 'Create Note'}
        </Button>
      </ModalFooter>
    </form>
  );
};

export const GlobalDialogProvider = ({ children }: { children: ReactNode }) => {
  const [dialogAction, setDialogAction] = useState<GlobalDialogAction | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const openDialog = useCallback((action: GlobalDialogAction) => {
    setDialogAction(action);
  }, []);
    const closeDialog = useCallback(() => setDialogAction(null), []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const compose = params.get('compose');

    if (!compose) {
      return;
    }

    if (compose === 'new-task') {
      openDialog({
        type: 'new-task',
        data: {
          projectId: params.get('projectId') || undefined,
          boardId: params.get('boardId') || undefined,
          title: params.get('title') || undefined,
          clarifyingInfo: params.get('clarifyingInfo') || undefined,
          sourceUrl: params.get('sourceUrl') || undefined,
          sourceTitle: params.get('sourceTitle') || undefined,
          sourceText: params.get('sourceText') || undefined,
          sourceType: params.get('sourceType') || undefined
        }
      });
    }

    if (compose === 'add-note') {
      openDialog({
        type: 'add-note',
        data: {
          title: params.get('title') || undefined,
          content: params.get('content') || undefined,
          sourceUrl: params.get('sourceUrl') || undefined,
          sourceTitle: params.get('sourceTitle') || undefined,
          sourceText: params.get('sourceText') || undefined,
          sourceType: params.get('sourceType') || undefined
        }
      });
    }

    params.delete('compose');
    params.delete('projectId');
    params.delete('boardId');
    params.delete('title');
    params.delete('clarifyingInfo');
    params.delete('content');
    params.delete('sourceUrl');
    params.delete('sourceTitle');
    params.delete('sourceText');
    params.delete('sourceType');
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : ''
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate, openDialog]);

  // --- Main Dialog Render ---
  return (
    <GlobalDialogContext.Provider value={{ openDialog, closeDialog }}>
      {children}
      <Modal isOpen={!!dialogAction} onClose={closeDialog}>
        {dialogAction?.type === 'create-event' && <CreateEventDialog onClose={closeDialog} />}
        {dialogAction?.type === 'new-task' && <NewTaskDialog onClose={closeDialog} initialData={dialogAction.data} />}
        {dialogAction?.type === 'add-note' && <AddNoteDialog onClose={closeDialog} initialData={dialogAction.data} />}
        {/* Future extensibility: */}
        {dialogAction?.type === 'custom' && (
          <div>
            <ModalHeader onClose={closeDialog}>
              <ModalTitle>Custom Action</ModalTitle>
            </ModalHeader>
            <ModalContent>
              <div>Custom dialog for key: {dialogAction.key}</div>
            </ModalContent>
          </div>
        )}
      </Modal>
    </GlobalDialogContext.Provider>
  );
}; 
